import { useEffect, useState } from 'react';
import { Carte, Etiquette, Tableau } from '@journal/ui';
import { useSession } from '../lib/session.tsx';

/**
 * Les factures de l'école, et les avoirs qui les corrigent.
 *
 * ── Pourquoi ici, et pas seulement dans le portail Stripe ──────────────────
 * Le portail montre les factures ; un avoir y reste enfoui dans la page de la
 * facture qu'il corrige. Or c'est la pièce que réclame le trésorier d'une
 * coopérative scolaire ou la mairie qui a réglé : « vous nous avez remboursé
 * 20 €, où est le document ? ». Elle doit se trouver en un clic, à côté de la
 * facture.
 *
 * Rien ne s'affiche tant qu'il n'y a rien : un tableau vide pendant l'essai
 * ne dit rien d'utile.
 */

interface Facture {
  number: string | null;
  amountTotal: number;
  currency: string;
  status: string;
  pdfUrl: string | null;
  issuedAt: string;
}

interface Avoir {
  number: string | null;
  invoiceNumber: string | null;
  amount: number;
  refundAmount: number;
  creditAmount: number;
  outOfBandAmount: number;
  currency: string;
  status: string;
  pdfUrl: string | null;
  issuedAt: string;
}

interface Ligne {
  cle: string;
  nature: 'facture' | 'avoir';
  numero: string;
  detail: string;
  montant: string;
  etat: { texte: string; ton: 'succes' | 'attention' | 'info' | 'erreur' };
  date: string;
  pdf: string | null;
}

const ETAT_FACTURE: Record<string, Ligne['etat']> = {
  paid: { texte: 'Payée', ton: 'succes' },
  open: { texte: 'À payer', ton: 'attention' },
  void: { texte: 'Annulée', ton: 'info' },
  uncollectible: { texte: 'Impayée', ton: 'erreur' },
};

const euros = (centimes: number, devise: string) =>
  (centimes / 100).toLocaleString('fr-FR', { style: 'currency', currency: devise.toUpperCase() });

const date = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** Ce que l'avoir a fait de l'argent, dit simplement. */
function destination(a: Avoir): string {
  if (a.refundAmount > 0) return 'Remboursé sur votre moyen de paiement';
  if (a.creditAmount > 0) return 'Déduit de vos prochaines factures';
  return 'Remboursé par virement ou chèque';
}

export function FacturesEtAvoirs() {
  const { api } = useSession();
  const [lignes, setLignes] = useState<Ligne[] | null>(null);

  useEffect(() => {
    let vivant = true;
    Promise.all([
      api.requete<Facture[]>('/billing/invoices').catch(() => [] as Facture[]),
      api.requete<Avoir[]>('/billing/credit-notes').catch(() => [] as Avoir[]),
    ]).then(([factures, avoirs]) => {
      if (!vivant) return;
      const toutes: Array<Ligne & { tri: number }> = [
        ...factures.map((f, i) => ({
          cle: `f-${f.number ?? i}-${f.issuedAt}`,
          nature: 'facture' as const,
          numero: f.number ?? '—',
          detail: 'Facture',
          montant: euros(f.amountTotal, f.currency),
          etat: ETAT_FACTURE[f.status] ?? { texte: f.status, ton: 'info' as const },
          date: date(f.issuedAt),
          pdf: f.pdfUrl,
          tri: new Date(f.issuedAt).getTime(),
        })),
        ...avoirs.map((a, i) => ({
          cle: `a-${a.number ?? i}-${a.issuedAt}`,
          nature: 'avoir' as const,
          numero: a.number ?? '—',
          detail: `Avoir${a.invoiceNumber ? ` sur la facture ${a.invoiceNumber}` : ''} : ${destination(a)}`,
          montant: `− ${euros(a.amount, a.currency)}`,
          etat: a.status === 'void'
            ? { texte: 'Annulé', ton: 'info' as const }
            : { texte: 'Émis', ton: 'succes' as const },
          date: date(a.issuedAt),
          pdf: a.pdfUrl,
          tri: new Date(a.issuedAt).getTime(),
        })),
      ];
      setLignes(toutes.sort((x, y) => y.tri - x.tri));
    });
    return () => { vivant = false; };
  }, [api]);

  if (!lignes || lignes.length === 0) return null;

  return (
    <Carte titre="Factures et avoirs">
      <Tableau
        legende="Vos factures et avoirs, les plus récents en tête"
        redimensionnable="factures-avoirs"
        lignes={lignes}
        cleLigne={(l) => l.cle}
        colonnes={[
          { cle: 'date', entete: 'Date', largeur: '10rem', cellule: (l) => l.date },
          { cle: 'numero', entete: 'Numéro', largeur: '10rem', cellule: (l) => l.numero },
          { cle: 'detail', entete: 'Document', cellule: (l) => l.detail },
          { cle: 'montant', entete: 'Montant', largeur: '8rem', numerique: true, cellule: (l) => l.montant },
          {
            cle: 'etat', entete: 'État', largeur: '7rem',
            cellule: (l) => <Etiquette ton={l.etat.ton}>{l.etat.texte}</Etiquette>,
          },
          {
            cle: 'pdf', entete: '', largeur: '6rem',
            cellule: (l) => (l.pdf ? (
              <a
                href={l.pdf}
                target="_blank"
                rel="noreferrer noopener"
                className="rounded-sm text-[var(--color-primary)] underline underline-offset-2"
              >
                Télécharger
              </a>
            ) : null),
          },
        ]}
      />
    </Carte>
  );
}
